import { useMemo } from "react";
import { Column } from "primereact/column";
import localeText from "../../../../locale";
import AdminService from "../../../../service/AdminService";
import { buildSearchText, getToken } from "../../utils/adminUtils";
import CatalogTable from "../../components/list/CatalogTable";
import CopyValue from "../../components/list/CopyValue";
import useReport from "./hooks/useReport";
import ReportToolbar from "./components/ReportToolbar";
import ReportSummary from "./components/ReportSummary";
import { firstValue, formatMoney, formatReportDate, toReportRows, toTimestamp } from "./reportUtils";
import "../AdminHome/AdminHome.css";
import "./AdminReports.css";

// Mesmos nomes alternativos de campo aceitos pelo relatório clássico
const FIELDS = {
  firstname: ["firstname", "name", "user.firstname", "user.name"],
  surname: ["surname", "lastname", "user.surname", "user.lastname"],
  email: ["email", "user.email", "username", "user.username", "paypalEmailToTransfer"],
  paypal: ["paypalEmailToTransfer", "paypalEmail"],
  iban: ["iban", "bankIban"],
  bankTarget: ["accountOwner", "bankname", "paymentMethod", "destination"],
  currency: ["currency", "coin", "currencyCode"],
  requested: ["amountRequested", "amount", "requestedAmount"],
  net: ["amountToDeposit", "netAmount", "amountNet"],
  date: ["dateRequest", "requestDate"],
};

const loadWithdrawals = async (dateBegin, dateEnd) =>
  toReportRows(await new AdminService().getCompletedWithdrawalsReport(getToken(), dateBegin, dateEnd));

const loadWithdrawalsPdf = (dateBegin, dateEnd) =>
  new AdminService().getCompletedWithdrawalsReportPdf(getToken(), dateBegin, dateEnd);

// Soma por moeda, para não misturar EUR com outras moedas num total só
const totalsByCurrency = (rows, field) => {
  const totals = rows.reduce((acc, row) => {
    const value = Number(row[field]);
    if (!Number.isFinite(value)) return acc;
    acc[row.currency] = (acc[row.currency] || 0) + value;
    return acc;
  }, {});
  const entries = Object.entries(totals);
  return entries.length
    ? entries.map(([currency, total]) => formatMoney(total, currency)).join(" · ")
    : formatMoney(0);
};

const AdminCompletedWithdrawalsReport = () => {
  const report = useReport({
    load: loadWithdrawals,
    loadPdf: loadWithdrawalsPdf,
    pdfFileName: "completed-withdrawals-report",
  });
  const { rows: rawRows, loading, loadError, reload } = report;

  const rows = useMemo(
    () =>
      rawRows.map((row, index) => {
        const paypal = firstValue(row, FIELDS.paypal);
        const iban = firstValue(row, FIELDS.iban);
        const firstname = firstValue(row, FIELDS.firstname);
        const surname = firstValue(row, FIELDS.surname);
        const email = firstValue(row, FIELDS.email);
        const rawDate = firstValue(row, FIELDS.date);
        return {
          ...row,
          id: row.id ?? `row-${index}`,
          fullName: `${firstname || ""} ${surname || ""}`.trim(),
          contact: email,
          paypal,
          iban,
          bankTarget: firstValue(row, FIELDS.bankTarget),
          destinationType: paypal ? "PayPal" : iban ? "Bank" : null,
          currency: firstValue(row, FIELDS.currency) || "EUR",
          requested: firstValue(row, FIELDS.requested),
          net: firstValue(row, FIELDS.net),
          netNumber: Number(firstValue(row, FIELDS.net)) || 0,
          rawDate,
          dateValue: toTimestamp(rawDate),
          searchText: buildSearchText([firstname, surname, email, paypal, iban]),
        };
      }),
    [rawRows],
  );

  const summary = [
    { label: localeText("adminReportsWithdrawalsCount"), value: rows.length.toLocaleString() },
    { label: localeText("completedWithdrawalsColRequestedValue"), value: totalsByCurrency(rows, "requested") },
    { label: localeText("completedWithdrawalsColNetValue"), value: totalsByCurrency(rows, "net") },
  ];

  return (
    <div className="admin-list admin-report">
      <ReportToolbar report={report} />
      <ReportSummary items={summary} loading={loading} />

      <CatalogTable
        rows={rows}
        loading={loading}
        loadError={loadError}
        onReload={reload}
        searchPlaceholder={localeText("adminReportsSearchWithdrawals")}
        sortField="dateValue"
        sortOrder={-1}
        minWidth="60rem"
      >
        <Column
          header={localeText("name")}
          field="fullName"
          body={(row) => (
            <div className="admin-stack">
              <span className="admin-strong">{row.fullName || "—"}</span>
              {row.contact && <span className="admin-muted">{row.contact}</span>}
            </div>
          )}
          sortable
          style={{ minWidth: "14rem" }}
        />
        <Column
          header={localeText("completedWithdrawalsHeaderRequestDate")}
          field="dateValue"
          body={(row) => formatReportDate(row.rawDate)}
          sortable
        />
        <Column
          header={localeText("completedWithdrawalsColPayment")}
          body={(row) =>
            row.paypal ? (
              <div className="admin-stack">
                <span className="admin-muted">PayPal</span>
                <CopyValue value={row.paypal} />
              </div>
            ) : (
              <div className="admin-stack">
                <span className="admin-muted">{row.destinationType === "Bank" ? localeText("bank") : "—"}</span>
                {row.bankTarget && <span>{row.bankTarget}</span>}
                {row.iban && <CopyValue value={row.iban} mono />}
              </div>
            )
          }
          style={{ minWidth: "16rem" }}
        />
        <Column
          header={localeText("completedWithdrawalsColNetValue")}
          field="netNumber"
          body={(row) => (
            <div className="admin-stack">
              <span className="admin-num admin-strong">{formatMoney(row.net, row.currency)}</span>
              <span className="admin-num admin-muted">
                {localeText("adminWithdrawRequestedShort")} {formatMoney(row.requested, row.currency)}
              </span>
            </div>
          )}
          sortable
        />
      </CatalogTable>
    </div>
  );
};

export default AdminCompletedWithdrawalsReport;
